<template>
  <div class="rollcall-mobile">
    <TopNavBarMobile :classId="classId" :subjectIndex="subjectIndex" />
    
    <div class="rollcall-header">
      <el-button class="back-btn" @click="goBack" icon="el-icon-arrow-left" size="small">返回</el-button>
      <span class="page-title">点名</span>
    </div>
    
    <div class="rollcall-content">
      <div class="count-control">
        <div class="count-label">点名人数：</div>
        <el-input-number 
          v-model="selectedCount" 
          :min="1" 
          :max="maxCount" 
          :step="1"
          size="medium"
          :disabled="isSpinning"
          @change="updatePointers"
          style="width: 120px; margin-right: 5px;"
        />
        <el-button 
          size="medium" 
          @click="decreaseCount"
          :disabled="selectedCount <= 1 || isSpinning"
          style="padding: 10px 14px; font-size: 14px;">
          -10
        </el-button>
        <el-button 
          size="medium" 
          @click="increaseCount"
          :disabled="selectedCount >= maxCount || isSpinning"
          style="padding: 10px 14px; font-size: 14px;">
          +10
        </el-button>
      </div>
      
      <div class="exclude-control">
        <el-button 
          size="medium" 
          @click="excludeCurrentStudents"
          :disabled="isSpinning || !hasStarted || selectedStudents.length === 0 || displayStudents.length <= 1"
          style="padding: 12px 20px; font-size: 15px;">
          排除当前学生
        </el-button>
        <el-button 
          size="medium" 
          @click="showExcludedListDialog = true"
          :disabled="excludedStudentIds.length === 0"
          style="padding: 12px 20px; font-size: 15px;">
          被排除学生列表 ({{ excludedStudentIds.length }})
        </el-button>
      </div>
      
      <div class="result-display" :style="{ background: subjectGradient }">
        <div v-if="selectedCount === 1" class="result-single">
          <div class="result-label">选中学生：</div>
          <div class="result-name">{{ hasStarted ? (selectedStudent ? (selectedStudent.studentNumber ? `${selectedStudent.studentNumber}. ${selectedStudent.name}` : selectedStudent.name) : '未开始') : '未开始' }}</div>
        </div>
        <div v-else class="result-multiple">
          <div class="result-label">选中学生：</div>
          <div class="result-list">
            <div 
              v-for="(student, index) in selectedStudents" 
              :key="index"
              class="result-item">
              <span 
                class="pointer-color" 
                :style="{ backgroundColor: pointerColors[index % pointerColors.length] }">
              </span>
              <span class="result-name">{{ hasStarted ? (student ? (student.studentNumber ? `${student.studentNumber}. ${student.name}` : student.name) : '未开始') : '未开始' }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="warningMessage" class="warning-message">{{ warningMessage }}</div>
      
      <div class="wheel-wrapper">
        <canvas ref="wheelCanvas" class="wheel-canvas"></canvas>
      </div>
      
      <div class="controls">
        <el-button type="primary" size="large" @click="toggleSpin" class="control-btn">
          {{ isSpinning ? '停止' : '开始' }}
        </el-button>
        <el-button size="large" @click="reset" class="control-btn">
          重置
        </el-button>
      </div>
      
      <div class="tips">提示：点击按钮或拖动转盘转动，点击按钮或转盘停止</div>
    </div>
    
    <el-dialog
      :visible.sync="showExcludedListDialog"
      title="被排除学生列表"
      width="90%"
      :close-on-click-modal="true">
      <div class="excluded-list-container">
        <div v-if="excludedStudents.length === 0" class="empty-state">
          暂无被排除的学生
        </div>
        <div v-else class="excluded-list">
          <div 
            v-for="student in excludedStudents" 
            :key="student.id"
            class="excluded-student-item">
            <div class="student-info">
              <span class="student-number">{{ student.studentNumber ? student.studentNumber : '' }}</span>
              <span class="student-name">{{ student.name }}</span>
            </div>
            <el-button 
              size="small" 
              type="primary"
              @click="restoreStudent(student.id)">
              恢复
            </el-button>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="showExcludedListDialog = false">关闭</el-button>
        <el-button 
          v-if="excludedStudents.length > 0"
          type="primary"
          @click="restoreAllStudents">
          全部恢复
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import TopNavBarMobile from '@/components/mobile/TopNavBarMobile.vue'

export default {
  name: 'RollCallMobile',
  components: {
    TopNavBarMobile
  },
  props: {
    classId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      subjectIndex: 0,
      students: [],
      rotation: 0,
      speed: 0,
      isSpinning: false,
      isDragging: false,
      lastTouchAngle: 0,
      lastTouchTime: 0,
      lastTouchSpeed: 0,
      selectedStudent: null,
      selectedStudents: [],
      selectedCount: 1,
      pointerAngles: [],
      hasStarted: false,
      excludedStudentIds: [],
      showExcludedListDialog: false,
      warningMessage: '',
      originalStudents: [],
      pointerColors: [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
        '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
        '#BB8FCE', '#85C1E9', '#F8B500', '#00CED1'
      ],
      colors: [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
        '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
        '#BB8FCE', '#85C1E9', '#F8B500', '#00CED1'
      ],
      animationId: null,
      boundEvents: null,
      canvasSize: 280
    }
  },
  computed: {
    subjectGradient() {
      return 'linear-gradient(90deg, #40e0d0, #6abfd1)';
    },
    maxCount() {
      return Math.max(1, this.students.length - 1);
    },
    displayStudents() {
      if (this.excludedStudentIds.length > 0) {
        return this.students.filter(s => !this.excludedStudentIds.includes(s.id));
      } else {
        return this.students;
      }
    },
    excludedStudents() {
      return this.originalStudents.filter(s => this.excludedStudentIds.includes(s.id));
    }
  },
  created() {
    this.loadSubjectIndex();
    this.loadStudents();
  },
  mounted() {
    this.$nextTick(() => {
      this.initCanvas();
      this.setupWheelEvents();
    });
  },
  beforeDestroy() {
    this.removeWheelEvents();
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  },
  methods: {
    loadSubjectIndex() {
      const savedState = sessionStorage.getItem('mobileNavState');
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          this.subjectIndex = state.subjectIndex || 0;
        } catch (e) {
          this.subjectIndex = 0;
        }
      }
    },
    loadStudents() {
      this.$http.get(`/students/class?classId=${this.classId}`).then(response => {
        this.students = response.data.sort((a, b) => {
          const numA = parseInt(a.studentNumber, 10) || 0;
          const numB = parseInt(b.studentNumber, 10) || 0;
          return numA - numB;
        });
        this.originalStudents = [...this.students];
        this.$nextTick(() => {
          this.updatePointers();
        });
      }).catch(error => {
        console.error('加载学生列表失败:', error);
        this.$message.error('加载学生列表失败');
      });
    },
    goBack() {
      this.$router.push(`/mobile/more/${this.classId}`);
    },
    initCanvas() {
      const canvas = this.$refs.wheelCanvas;
      if (!canvas) return;
      
      const containerWidth = Math.min(window.innerWidth - 40, 320);
      this.canvasSize = containerWidth;
      canvas.width = containerWidth;
      canvas.height = containerWidth;
      canvas.style.width = containerWidth + 'px';
      canvas.style.height = containerWidth + 'px';
      
      this.updatePointers();
    },
    drawWheel() {
      const canvas = this.$refs.wheelCanvas;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radius = Math.min(centerX, centerY) - 10;
      
      const students = this.displayStudents;
      if (students.length === 0) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#f5f7fa';
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = '#909399';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('暂无学生', centerX, centerY);
        return;
      }
      
      const anglePerStudent = (2 * Math.PI) / students.length;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      students.forEach((student, index) => {
        const startAngle = index * anglePerStudent + this.rotation;
        const endAngle = startAngle + anglePerStudent;
        
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        ctx.closePath();
        
        ctx.fillStyle = this.colors[index % this.colors.length];
        ctx.fill();
        
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(startAngle + anglePerStudent / 2);
        ctx.textAlign = 'right';
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 12px Arial';
        
        const studentInfo = student.studentNumber ? `${student.studentNumber}. ${student.name}` : student.name;
        const maxLength = 8;
        const displayInfo = studentInfo.length > maxLength ? studentInfo.substring(0, maxLength) : studentInfo;
        ctx.fillText(displayInfo, radius - 15, 4);
        
        ctx.restore();
      });
      
      this.drawCenterCircle(ctx, centerX, centerY, 20);
      
      if (this.selectedCount === 1) {
        this.drawPointer(ctx, centerX, centerY, radius, 0);
      } else {
        this.drawPointers(ctx, centerX, centerY, radius);
      }
    },
    drawCenterCircle(ctx, x, y, radius) {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2;
      ctx.stroke();
    },
    
    drawPointers(ctx, centerX, centerY, wheelRadius) {
      const pointerCount = this.selectedCount;
      
      for (let i = 0; i < pointerCount; i++) {
        const angle = this.pointerAngles[i];
        const color = this.pointerColors[i % this.pointerColors.length];
        this.drawPointer(ctx, centerX, centerY, wheelRadius, angle, color);
      }
    },
    
    drawPointer(ctx, centerX, centerY, wheelRadius, angle, color) {
      const pointerLength = 20;
      const pointerWidth = 20;
      
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(angle);
      
      ctx.beginPath();
      ctx.moveTo(wheelRadius, 0);
      ctx.lineTo(wheelRadius + pointerLength, -pointerWidth / 2);
      ctx.lineTo(wheelRadius + pointerLength, pointerWidth / 2);
      ctx.closePath();
      
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 1;
      ctx.stroke();
      
      ctx.restore();
    },
    
    toggleSpin() {
      if (this.isDragging) return;
      if (this.isSpinning) {
        this.stopSpin();
      } else {
        this.startSpin();
      }
    },
    startSpin() {
      if (this.displayStudents.length === 0) {
        this.$message.warning('暂无学生，无法开始点名');
        return;
      }
      this.isSpinning = true;
      this.speed = 0.6 + Math.random() * 0.6;
      this.selectedStudent = null;
      this.selectedStudents = [];
      this.hasStarted = true;
      this.animate();
    },
    stopSpin() {
      this.isSpinning = false;
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }
      this.determineWinner();
    },
    animate() {
      if (!this.isSpinning) return;
      
      this.rotation += this.speed;
      this.speed *= 0.96;
      
      if (this.speed < 0.002 && this.speed > -0.002) {
        this.stopSpin();
        return;
      }
      
      this.drawWheel();
      this.updateSelectedStudent();
      this.animationId = requestAnimationFrame(this.animate);
    },
    updateSelectedStudent() {
      const students = this.displayStudents;
      if (students.length === 0) return;
      
      const anglePerStudent = (2 * Math.PI) / students.length;
      const normalizedRotation = this.rotation % (2 * Math.PI);
      
      this.selectedStudents = [];
      for (let i = 0; i < this.selectedCount; i++) {
        let pointerAngle;
        if (this.selectedCount === 1) {
          pointerAngle = -normalizedRotation;
        } else {
          pointerAngle = this.pointerAngles[i] - normalizedRotation;
        }
        
        let winnerIndex = Math.floor(pointerAngle / anglePerStudent);
        if (winnerIndex < 0) {
          winnerIndex += students.length;
        }
        winnerIndex = winnerIndex % students.length;
        
        this.selectedStudents.push(students[winnerIndex]);
      }
      
      if (this.selectedCount === 1) {
        this.selectedStudent = this.selectedStudents[0];
      }
    },
    determineWinner() {
      const students = this.displayStudents;
      if (students.length === 0) return;
      
      const anglePerStudent = (2 * Math.PI) / students.length;
      const normalizedRotation = this.rotation % (2 * Math.PI);
      
      this.selectedStudents = [];
      for (let i = 0; i < this.selectedCount; i++) {
        let pointerAngle;
        if (this.selectedCount === 1) {
          pointerAngle = -normalizedRotation;
        } else {
          pointerAngle = this.pointerAngles[i] - normalizedRotation;
        }
        
        let winnerIndex = Math.floor(pointerAngle / anglePerStudent);
        if (winnerIndex < 0) {
          winnerIndex += students.length;
        }
        winnerIndex = winnerIndex % students.length;
        
        this.selectedStudents.push(students[winnerIndex]);
      }
      
      if (this.selectedCount === 1) {
        this.selectedStudent = this.selectedStudents[0];
      }
    },
    reset() {
      this.rotation = Math.random() * 2 * Math.PI;
      this.speed = 0;
      this.isSpinning = false;
      this.selectedStudent = null;
      this.selectedStudents = [];
      this.hasStarted = false;
      this.warningMessage = '';
      this.pointerAngles = this.generateRandomPointers();
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }
      this.drawWheel();
      this.updateSelectedStudent();
    },
    
    updatePointers() {
      const displayStudents = this.displayStudents;
      if (displayStudents.length === 0) return;
      
      this.selectedStudent = null;
      this.selectedStudents = [];
      this.hasStarted = false;
      
      if (displayStudents.length <= this.selectedCount) {
        const compareText = displayStudents.length < this.selectedCount ? '大于' : '等于';
        this.warningMessage = `点名人数(${this.selectedCount}人)${compareText}转盘上剩余的学生人数(${displayStudents.length}人)，将会抽到重复的学生！`;
      } else {
        this.warningMessage = '';
      }
      
      this.pointerAngles = this.generateRandomPointers();
      this.drawWheel();
      this.updateSelectedStudent();
    },
    
    generateRandomPointers() {
      const displayStudents = this.displayStudents;
      if (displayStudents.length === 0) return [];
      
      const pointerCount = this.selectedCount;
      if (pointerCount === 1) {
        return [0];
      }
      
      const anglePerStudent = (2 * Math.PI) / displayStudents.length;
      const minAngleBetweenPointers = anglePerStudent * 1.5;
      
      const pointerAngles = [];
      const maxAttempts = 100;
      
      for (let i = 0; i < pointerCount; i++) {
        let angle;
        let attempts = 0;
        let found = false;
        
        while (attempts < maxAttempts && !found) {
          angle = Math.random() * 2 * Math.PI;
          
          if (this.isValidPointerAngle(angle, pointerAngles, minAngleBetweenPointers)) {
            pointerAngles.push(angle);
            found = true;
          }
          
          attempts++;
        }
        
        if (!found) {
          let bestAngle = 0;
          let maxMinDistance = 0;
          
          for (let testAngle = 0; testAngle < 2 * Math.PI; testAngle += 0.1) {
            let minDistance = 2 * Math.PI;
            
            for (const existingAngle of pointerAngles) {
              const distance = Math.abs(testAngle - existingAngle);
              const circularDistance = Math.min(distance, 2 * Math.PI - distance);
              minDistance = Math.min(minDistance, circularDistance);
            }
            
            if (minDistance > maxMinDistance) {
              maxMinDistance = minDistance;
              bestAngle = testAngle;
            }
          }
          
          pointerAngles.push(bestAngle);
        }
      }
      
      return pointerAngles;
    },
    
    isValidPointerAngle(newAngle, existingAngles, minAngleBetweenPointers) {
      for (const existingAngle of existingAngles) {
        const distance = Math.abs(newAngle - existingAngle);
        const circularDistance = Math.min(distance, 2 * Math.PI - distance);
        
        if (circularDistance < minAngleBetweenPointers) {
          return false;
        }
      }
      return true;
    },
    
    increaseCount() {
      if (this.selectedCount + 10 <= this.maxCount) {
        this.selectedCount += 10;
      } else {
        this.selectedCount = this.maxCount;
      }
      this.updatePointers();
    },
    
    decreaseCount() {
      if (this.selectedCount - 10 >= 1) {
        this.selectedCount -= 10;
      } else {
        this.selectedCount = 1;
      }
      this.updatePointers();
    },
    
    excludeCurrentStudents() {
      if (this.hasStarted && this.selectedStudents.length > 0) {
        const newExcludedIds = this.selectedStudents.map(s => s.id);
        this.excludedStudentIds = [...this.excludedStudentIds, ...newExcludedIds];
        this.updatePointers();
        this.drawWheel();
        
        if (this.displayStudents.length < this.selectedCount) {
          this.warningMessage = `点名人数(${this.selectedCount}人)大于转盘上剩余的学生人数(${this.displayStudents.length}人)，将会抽到重复的学生！`;
        } else {
          this.warningMessage = '';
        }
      }
    },
    
    restoreStudent(studentId) {
      this.excludedStudentIds = this.excludedStudentIds.filter(id => id !== studentId);
      this.warningMessage = '';
      this.updatePointers();
      this.drawWheel();
    },
    
    restoreAllStudents() {
      this.excludedStudentIds = [];
      this.warningMessage = '';
      this.updatePointers();
      this.drawWheel();
    },
    
    getTouchAngle(e) {
      const canvas = this.$refs.wheelCanvas;
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0] || e.changedTouches[0];
      const x = touch.clientX - rect.left - canvas.width / 2;
      const y = touch.clientY - rect.top - canvas.height / 2;
      let angle = Math.atan2(y, x);
      if (angle < 0) {
        angle += 2 * Math.PI;
      }
      return angle;
    },
    handleTouchStart(e) {
      e.preventDefault();
      this.isDragging = true;
      this.lastTouchAngle = this.getTouchAngle(e);
      this.lastTouchTime = Date.now();
      this.lastTouchSpeed = 0;
      this.speed = 0;
      this.isSpinning = false;
      this.hasStarted = true;
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }
      this.drawWheel();
      this.updateSelectedStudent();
    },
    handleTouchMove(e) {
      e.preventDefault();
      if (!this.isDragging) return;
      
      const currentTime = Date.now();
      const currentAngle = this.getTouchAngle(e);
      let deltaAngle = currentAngle - this.lastTouchAngle;
      
      if (deltaAngle > Math.PI) {
        deltaAngle -= 2 * Math.PI;
      } else if (deltaAngle < -Math.PI) {
        deltaAngle += 2 * Math.PI;
      }
      
      const deltaTime = currentTime - this.lastTouchTime;
      if (deltaTime > 0) {
        this.lastTouchSpeed = deltaAngle / deltaTime * 100;
      }
      
      this.rotation += deltaAngle;
      this.lastTouchAngle = currentAngle;
      this.lastTouchTime = currentTime;
      
      this.drawWheel();
      this.updateSelectedStudent();
    },
    handleTouchEnd(e) {
      e.preventDefault();
      if (this.isDragging) {
        this.isDragging = false;
        if (Math.abs(this.lastTouchSpeed) > 0.01) {
          this.speed = this.lastTouchSpeed;
          this.isSpinning = true;
          this.animate();
        }
      }
    },
    setupWheelEvents() {
      const canvas = this.$refs.wheelCanvas;
      if (!canvas) return;
      
      this.boundEvents = {
        touchstart: (e) => this.handleTouchStart(e),
        touchmove: (e) => this.handleTouchMove(e),
        touchend: (e) => this.handleTouchEnd(e),
        touchcancel: (e) => this.handleTouchEnd(e),
        mousedown: (e) => this.handleMouseDown(e),
        mousemove: (e) => this.handleMouseMove(e),
        mouseup: (e) => this.handleMouseUp(e),
        mouseleave: (e) => this.handleMouseLeave(e)
      };
      
      Object.keys(this.boundEvents).forEach(event => {
        canvas.addEventListener(event, this.boundEvents[event], { passive: false });
      });
    },
    removeWheelEvents() {
      const canvas = this.$refs.wheelCanvas;
      if (canvas && this.boundEvents) {
        Object.keys(this.boundEvents).forEach(event => {
          canvas.removeEventListener(event, this.boundEvents[event]);
        });
      }
    },
    getMouseAngle(e) {
      const canvas = this.$refs.wheelCanvas;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left - canvas.width / 2;
      const y = e.clientY - rect.top - canvas.height / 2;
      let angle = Math.atan2(y, x);
      if (angle < 0) {
        angle += 2 * Math.PI;
      }
      return angle;
    },
    handleMouseDown(e) {
      e.preventDefault();
      this.isDragging = true;
      this.lastTouchAngle = this.getMouseAngle(e);
      this.lastTouchTime = Date.now();
      this.lastTouchSpeed = 0;
      this.speed = 0;
      this.isSpinning = false;
      this.hasStarted = true;
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }
      this.drawWheel();
      this.updateSelectedStudent();
    },
    handleMouseMove(e) {
      e.preventDefault();
      if (!this.isDragging) return;
      
      const currentTime = Date.now();
      const currentAngle = this.getMouseAngle(e);
      let deltaAngle = currentAngle - this.lastTouchAngle;
      
      if (deltaAngle > Math.PI) {
        deltaAngle -= 2 * Math.PI;
      } else if (deltaAngle < -Math.PI) {
        deltaAngle += 2 * Math.PI;
      }
      
      const deltaTime = currentTime - this.lastTouchTime;
      if (deltaTime > 0) {
        this.lastTouchSpeed = deltaAngle / deltaTime * 100;
      }
      
      this.rotation += deltaAngle;
      this.lastTouchAngle = currentAngle;
      this.lastTouchTime = currentTime;
      
      this.drawWheel();
      this.updateSelectedStudent();
    },
    handleMouseUp(e) {
      e.preventDefault();
      if (this.isDragging) {
        this.isDragging = false;
        if (Math.abs(this.lastTouchSpeed) > 0.01) {
          this.speed = this.lastTouchSpeed;
          this.isSpinning = true;
          this.animate();
        }
      }
    },
    handleMouseLeave(e) {
      e.preventDefault();
      if (this.isDragging) {
        this.handleMouseUp(e);
      }
    }
  }
}
</script>

<style scoped>
.rollcall-mobile {
  min-height: 100vh;
  background: #f5f7fa;
  padding-top: 44px;
}

.rollcall-header {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
}

.back-btn {
  padding: 5px 10px;
}

.page-title {
  margin-left: 10px;
  font-size: 16px;
  font-weight: bold;
}

.rollcall-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px;
}

.result-display {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 320px;
  margin-bottom: 15px;
}

.result-label {
  color: #fff;
  font-size: 15px;
  font-weight: bold;
  margin-right: 10px;
}

.result-multiple .result-label {
  margin-bottom: 10px;
}

.result-single .result-label {
  margin-bottom: 0;
}

.result-single .result-name {
  color: #fff;
  font-size: 20px;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
}

.wheel-wrapper {
  position: relative;
  margin-bottom: 15px;
}

.wheel-canvas {
  border-radius: 50%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  cursor: grab;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  touch-action: none;
}

.wheel-canvas:active {
  cursor: grabbing;
}

.controls {
  display: flex;
  gap: 15px;
  width: 100%;
  max-width: 320px;
  justify-content: center;
}

.control-btn {
  flex: 1;
  padding: 12px 20px;
  font-size: 16px;
  border-radius: 12px;
}

.tips {
  margin-top: 15px;
  font-size: 13px;
  color: #909399;
  text-align: center;
}

.count-control {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  padding: 10px 12px;
  background: #f5f7fa;
  border-radius: 10px;
  width: 100%;
  max-width: 320px;
  margin-bottom: 0;
  flex-wrap: nowrap;
  justify-content: center;
}

.count-label {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  margin-right: 5px;
  flex-shrink: 0;
}

.exclude-control {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 10px;
  width: 100%;
  max-width: 320px;
  margin-bottom: 0;
  justify-content: center;
}

.result-single {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.result-multiple {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
}

.result-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  width: 100%;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
}

.pointer-color {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.result-multiple .result-name {
  color: #fff;
  font-size: 14px;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
}

.warning-message {
  background-color: #ff0000;
  color: #fff;
  font-size: 14px;
  font-weight: bold;
  padding: 8px 12px;
  margin-top: 10px;
  margin-bottom: 15px;
  text-align: center;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(255, 0, 0, 0.3);
  line-height: 1.4;
  white-space: pre-line;
  display: block;
  width: 100%;
  max-width: 320px;
  box-sizing: border-box;
  overflow: hidden;
}

.excluded-list-container {
  max-height: 400px;
  overflow-y: auto;
}

.empty-state {
  text-align: center;
  color: #909399;
  padding: 30px 20px;
  font-size: 14px;
}

.excluded-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.excluded-student-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 8px;
}

.student-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.student-number {
  font-size: 14px;
  color: #606266;
  font-weight: bold;
}

.student-name {
  font-size: 14px;
  color: #303133;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 10px;
}
</style>

<style>
.rollcall-mobile .el-button--primary:not(:disabled) {
  background: linear-gradient(90deg, #40e0d0, #6abfd1) !important;
  border-color: #40e0d0 !important;
  transition: none !important;
}

.rollcall-mobile .el-button--primary:not(:disabled):hover {
  background: linear-gradient(90deg, #66e8dc, #8ad0dd) !important;
  border-color: #66e8dc !important;
  transition: none !important;
}

.rollcall-mobile .el-button--primary:not(:disabled):active {
  background: linear-gradient(90deg, #3ac8c0, #5aa7c1) !important;
  border-color: #3ac8c0 !important;
  transition: none !important;
}

.rollcall-mobile .el-dialog__header {
  background: linear-gradient(90deg, #40e0d0, #6abfd1) !important;
  border-bottom: 1px solid #6abfd1 !important;
}

.rollcall-mobile .el-dialog__title {
  color: #fff !important;
}

.rollcall-mobile .el-dialog__headerbtn .el-dialog__close {
  color: #fff !important;
}

.rollcall-mobile .el-dialog__headerbtn .el-dialog__close:hover {
  color: #fff !important;
}
</style>
